import React, { useEffect, useState, useCallback } from "react";
import staffApi from "@/lib/staffApi";
import { PageHead } from "./DashboardLayout";
import { useStaff } from "@/context/StaffContext";
import { money } from "@/data/site";
import { apiError } from "@/lib/api";
import { toast } from "sonner";
import { X, Download, Search } from "lucide-react";

const STATUSES = ["processing", "paid", "shipped", "delivered", "cancelled"];

export default function AdminOrders() {
  const { can } = useStaff();
  const [orders, setOrders] = useState([]);
  const [selected, setSelected] = useState(null);
  const [status, setStatus] = useState("");
  const [search, setSearch] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [counts, setCounts] = useState({ all: 0 });

  const buildParams = useCallback(() => {
    const p = {};
    if (status) p.status = status;
    if (search.trim()) p.search = search.trim();
    if (dateFrom) p.date_from = dateFrom;
    if (dateTo) p.date_to = dateTo;
    return p;
  }, [status, search, dateFrom, dateTo]);

  const fetchData = useCallback(() => {
    staffApi.get("/admin/orders", { params: buildParams() }).then((r) => setOrders(r.data));
    const { status: _s, ...countParams } = buildParams();
    staffApi.get("/admin/orders/status-counts", { params: countParams }).then((r) => setCounts(r.data)).catch(() => {});
  }, [buildParams]);

  useEffect(() => {
    const t = setTimeout(fetchData, 250);
    return () => clearTimeout(t);
  }, [fetchData]);

  const clearFilters = () => { setStatus(""); setSearch(""); setDateFrom(""); setDateTo(""); };
  const hasFilters = status || search || dateFrom || dateTo;

  const exportCsv = async () => {
    try {
      const res = await staffApi.get("/admin/orders/export", { params: buildParams(), responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = "orders.csv"; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error("Export failed"); }
  };

  return (
    <div data-testid="admin-orders-page">
      <PageHead kicker="Fulfilment" title="Orders"
        action={<button onClick={exportCsv} data-testid="export-csv" className="flex items-center gap-2 border border-white/15 px-5 py-3 text-xs uppercase tracking-widest text-cream hover:border-gold hover:text-gold"><Download size={14} /> Export CSV</button>} />

      <div className="mb-4 flex flex-wrap gap-2" data-testid="orders-status-tabs">
        {[["", "All"], ...STATUSES.map((s) => [s, s])].map(([val, label]) => (
          <button key={val || "all"} onClick={() => setStatus(val)} data-testid={`status-tab-${val || "all"}`}
            className={`flex items-center gap-2 border px-3 py-1.5 text-xs uppercase tracking-widest transition ${status === val ? "border-gold bg-gold/10 text-gold" : "border-white/15 text-cream/60 hover:border-gold hover:text-gold"}`}>
            <span className="capitalize">{label}</span>
            <span className="font-mono text-[0.65rem] text-cream/40">{counts[val ? val : "all"] ?? 0}</span>
          </button>
        ))}
      </div>

      <div className="mb-6 flex flex-wrap items-end gap-3" data-testid="orders-filters">
        <label className="flex-1 min-w-[200px]">
          <span className="overline-label mb-1 block text-[0.6rem]">Search</span>
          <div className="relative">
            <Search size={14} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-cream/40" />
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Order #, email or customer"
              data-testid="orders-search" className="w-full border border-white/15 bg-black py-2 pl-9 pr-3 text-sm text-cream outline-none focus:border-gold" />
          </div>
        </label>
        <label>
          <span className="overline-label mb-1 block text-[0.6rem]">Status</span>
          <select value={status} onChange={(e) => setStatus(e.target.value)} data-testid="orders-status-filter"
            className="border border-white/15 bg-black px-3 py-2 text-sm text-cream outline-none focus:border-gold">
            <option value="" className="bg-black">All statuses</option>
            {STATUSES.map((s) => <option key={s} value={s} className="bg-black capitalize">{s}</option>)}
          </select>
        </label>
        <label>
          <span className="overline-label mb-1 block text-[0.6rem]">From</span>
          <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} data-testid="orders-date-from"
            className="border border-white/15 bg-black px-3 py-2 text-sm text-cream outline-none focus:border-gold" />
        </label>
        <label>
          <span className="overline-label mb-1 block text-[0.6rem]">To</span>
          <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} data-testid="orders-date-to"
            className="border border-white/15 bg-black px-3 py-2 text-sm text-cream outline-none focus:border-gold" />
        </label>
        {hasFilters && (
          <button onClick={clearFilters} data-testid="orders-clear-filters"
            className="border border-white/15 px-4 py-2 text-xs uppercase tracking-widest text-cream/60 hover:border-gold hover:text-gold">Clear</button>
        )}
      </div>

      <div className="overflow-x-auto border border-white/10">
        <table className="w-full min-w-[720px] text-sm" data-testid="orders-table">
          <thead><tr className="bg-ink text-gold/80">{["Order", "Date", "Customer", "Items", "Total", "Status", ""].map((h) => <th key={h} className="px-4 py-3 text-left font-mono text-[0.65rem] uppercase tracking-widest">{h}</th>)}</tr></thead>
          <tbody>
            {orders.length === 0 && <tr><td colSpan={7} className="px-4 py-10 text-center text-cream/50">{hasFilters ? "No orders match these filters." : "No orders yet."}</td></tr>}
            {orders.map((o) => (
              <tr key={o.id} className="border-t border-white/10 text-cream/80" data-testid={`order-row-${o.order_number}`}>
                <td className="px-4 py-3 font-mono text-gold">{o.order_number}</td>
                <td className="px-4 py-3 text-xs">{new Date(o.created_at).toLocaleDateString()}</td>
                <td className="px-4 py-3">{o.delivery?.full_name || o.email}</td>
                <td className="px-4 py-3">{o.items.reduce((s, i) => s + i.quantity, 0)}</td>
                <td className="px-4 py-3 font-mono">{money(o.total)}</td>
                <td className="px-4 py-3 capitalize">{o.status}</td>
                <td className="px-4 py-3"><button onClick={() => setSelected(o)} data-testid={`view-order-${o.order_number}`} className="border border-white/15 px-3 py-1.5 text-[0.6rem] uppercase tracking-widest hover:border-gold hover:text-gold">View</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {selected && <OrderModal order={selected} canEdit={can("admin")} onClose={() => setSelected(null)} onDone={() => { setSelected(null); fetchData(); }} />}
    </div>
  );
}

function OrderModal({ order, canEdit, onClose, onDone }) {
  const [status, setStatus] = useState(order.status);
  const [tracking, setTracking] = useState(order.tracking_code || "");
  const [trackingUrl, setTrackingUrl] = useState(order.tracking_url || "");
  const save = async () => {
    try { await staffApi.put(`/admin/orders/${order.id}/status`, { status, tracking_code: tracking || null, tracking_url: trackingUrl || null }); toast.success("Order updated"); onDone(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="order-modal">
      <div className="absolute inset-0 bg-black/85" onClick={onClose} />
      <div className="relative max-h-[90vh] w-full max-w-lg overflow-y-auto border border-gold/25 bg-ink p-6">
        <div className="mb-5 flex items-center justify-between">
          <div><p className="overline-label">{order.order_number}</p><h3 className="font-serif text-xl text-cream">Order Details</h3></div>
          <button onClick={onClose} aria-label="Close"><X size={18} className="text-cream/60" /></button>
        </div>
        <div className="mb-4 space-y-1 text-sm text-cream/70">
          <p><span className="text-cream/40">Customer:</span> {order.delivery?.full_name}</p>
          <p><span className="text-cream/40">Email:</span> {order.email}</p>
          <p><span className="text-cream/40">Address:</span> {order.delivery?.address}, {order.delivery?.city} {order.delivery?.postcode}, {order.delivery?.country}</p>
          <p><span className="text-cream/40">Phone:</span> {order.delivery?.phone}</p>
        </div>
        <div className="border-y border-white/10 py-4">
          {order.items.map((it, i) => (
            <div key={`${it.variant_id}-${i}`} className="flex items-center gap-3 py-2">
              <img src={it.image} alt={it.product_name} className="h-12 w-9 object-cover border border-white/10" />
              <div className="flex-1 text-sm"><p className="text-cream">{it.product_name}</p><p className="text-xs text-cream/40">{it.color}/{it.size} ×{it.quantity}</p></div>
              <span className="font-mono text-sm text-gold">{money(it.price * it.quantity)}</span>
            </div>
          ))}
        </div>
        <div className="flex justify-between py-3 text-sm"><span className="text-cream/60">Total</span><span className="font-mono text-lg text-gold">{money(order.total)}</span></div>

        {canEdit ? (
          <div className="mt-4 space-y-3 border-t border-white/10 pt-4">
            <label className="block"><span className="overline-label mb-1 block text-[0.6rem]">Status</span>
              <select value={status} onChange={(e) => setStatus(e.target.value)} data-testid="order-status-select" className="w-full border border-white/15 bg-black px-4 py-2 text-sm text-cream outline-none focus:border-gold">
                {STATUSES.map((s) => <option key={s} className="bg-black capitalize">{s}</option>)}
              </select></label>
            <label className="block"><span className="overline-label mb-1 block text-[0.6rem]">Tracking code</span>
              <input value={tracking} onChange={(e) => setTracking(e.target.value)} data-testid="order-tracking" className="w-full border border-white/15 bg-black px-4 py-2 text-sm text-cream outline-none focus:border-gold" /></label>
            <label className="block"><span className="overline-label mb-1 block text-[0.6rem]">Tracking link (courier URL)</span>
              <input value={trackingUrl} onChange={(e) => setTrackingUrl(e.target.value)} placeholder="https://tracking.courier.com/..." data-testid="order-tracking-url" className="w-full border border-white/15 bg-black px-4 py-2 text-sm text-cream outline-none focus:border-gold" /></label>
            <button onClick={save} data-testid="order-save" className="w-full bg-gold py-3 text-xs font-bold uppercase tracking-widest text-black hover:bg-gold-bright">Update Order</button>
          </div>
        ) : <p className="mt-4 text-xs text-cream/40">You have view-only access to order status.</p>}
      </div>
    </div>
  );
}
